import { EDDAction } from '../constants';
import { getPincodeFromCookie } from '../utils/utils.js';
import _isEmpty from 'lodash/isEmpty';

const eddDefaultValue = {
  isEDDFetching: false,
  isServiceabilityFetching: false,
  isCheckServiceabilityCalled: false,
  isCheckServiceabilityCalledForRex: false,
  errorInServiceabilityFetching: false,
  activePincode: getPincodeFromCookie(),
  eddDetails: {},
  serviceabilityDetails:{},
  // Multiseller Delivery SLA (checkDeliveryDetails v2), kept separate from the
  // v1 eddDetails slice. Shape: { [pincode]: { [skuId]: { options, isFetching, error } } }
  slaDetails: {},
  // B2C seller list (same call, fetchAllSellers=true). Same shape as slaDetails,
  // kept apart so the full list never replaces the PDP card's single seller.
  sellerListSla: {},
  isSlaFetching: false,
  // True when the last scopedView call errored out. Readers that must not serve
  // stale scopedView data (freebies) use this to fall back to the aggregator.
  slaFetchFailed: false,
  // Selected scopedView option (size + pincode). Drives offers/EMI when set.
  selectedSlaOption: null,
  // {
  //  [pincode]: {
  //     [productCode]:{
  //         "codEligible": true,
  //         "eddLower": "2019-04-08T14:29:51+0530",
  //         "eddUpper": "2019-04-10T14:29:51+0530",
  //         "productCode": "440610568029",
  //         "servicability": true
  //     }
  //   }
  //   .....
  // }
  isCartItemsEDDFetching: true,
  showPincodeModal: false,
  showMoveToClosetModal: false,
  movingAllNonDeliverableItemsToCloset: false,
  isCheckServiceabilityAPIFailed: false,
  isCheckDeliveryStatusCalled:false,
};

function edd(state = eddDefaultValue, action) {
  switch (action.type) {
    case EDDAction.SET.PINCODE_MODAL: {
      const { status } = action;
      return { ...state, showPincodeModal: status };
    }

    case EDDAction.SET.MOVE_TO_CLOSET: {
      const { status } = action;
      return { ...state, showMoveToClosetModal: status };
    }

    case EDDAction.SET.SELECTED_SLA_OPTION: {
      return {
        ...state,
        selectedSlaOption: action.option || null
      };
    }

    case EDDAction.RESET.DELIVERY_DETAILS: {
      const { productCode, isExchangeRquest } = action;
      // With pincode, scopedView will re-fetch — keep prior selectedSlaOption so offers/EMI
      // do not flash to aggregator variant data while the request is in flight.
      // Without pincode, clear so size-only path uses aggregator variant vouchers/emi.
      const hasPincode = Boolean(state.activePincode || getPincodeFromCookie());
      return {
        ...state,
        selectedSlaOption: hasPincode ? state.selectedSlaOption : null,
        eddDetails: {
          ...Object.entries(state.eddDetails).reduce(
            (updatedProductDetailsMapp, [pinCode, productDetailsMap]) => {
              let newMap = { ...updatedProductDetailsMapp, [pinCode]: {} };
              Object.entries(productDetailsMap).forEach(([pCode, details]) => {
                if (isExchangeRquest || pCode !== productCode) {
                  newMap = {
                    ...newMap,
                    [pinCode]: {
                      ...newMap[pinCode],
                      [pCode]: details
                    }
                  };
                }
              });
              return newMap;
            },
            {}
          )
        }
      };
    }

    case EDDAction.FETCH.DELIVERY_DETAILS.REQUEST: {
      const { pinCode, productCode } = action;

      function isUnservicablity(pinCodeDetails, productCode) {
        if (
          pinCodeDetails &&
          pinCodeDetails[productCode] &&
          pinCodeDetails[productCode].servicability
        ) {
          return pinCodeDetails[productCode];
        }
        return {};
      }

      return {
        ...state,
        isEDDFetching: true,
        activePincode: pinCode,
        eddDetails: {
          ...state.eddDetails,
          [pinCode]: {
            ...(state.eddDetails[pinCode] || {}),
            [productCode]: isUnservicablity(state.eddDetails[pinCode], productCode)
          }
        }
      };
    }
    case EDDAction.FETCH.DELIVERY_DETAILS.FAILURE: {
      return {
        ...state,
        isEDDFetching: false
      };
    }

    case EDDAction.FETCH.DELIVERY_DETAILS_V2.REQUEST: {
      const { pinCode, skuId } = action;
      // Keep previous selectedSlaOption until SUCCESS so offers/EMI stay stable mid-fetch.
      return {
        ...state,
        isSlaFetching: true,
        slaFetchFailed: false,
        slaDetails: {
          ...state.slaDetails,
          [pinCode]: {
            ...(state.slaDetails[pinCode] || {}),
            [skuId]: {
              ...((state.slaDetails[pinCode] || {})[skuId] || {}),
              isFetching: true,
              error: false
            }
          }
        }
      };
    }

    case EDDAction.FETCH.DELIVERY_DETAILS_V2.SUCCESS: {
      const {
        pinCode,
        skuId,
        requestedSkuId,
        options = [],
        selectedOption = null,
        hasMoreSellers = false
      } = action;
      const settledEntry = { options, isFetching: false, error: false, hasMoreSellers };
      return {
        ...state,
        isSlaFetching: false,
        slaFetchFailed: false,
        activePincode: pinCode,
        selectedSlaOption: selectedOption,
        slaDetails: {
          ...state.slaDetails,
          [pinCode]: {
            ...(state.slaDetails[pinCode] || {}),
            // The response may be keyed by a code other than the one requested.
            // Settle the requested key too, so readers can look up the sku they
            // asked for instead of scanning the pincode for any populated entry.
            ...(requestedSkuId && requestedSkuId !== skuId
              ? { [requestedSkuId]: settledEntry }
              : {}),
            [skuId]: settledEntry
          }
        }
      };
    }

    case EDDAction.FETCH.DELIVERY_DETAILS_V2.FAILURE: {
      const { pinCode, skuId } = action;
      // Keep prior selectedSlaOption on failure — do not snap back to aggregator mid-session.
      // Freebies are the exception: they read slaFetchFailed and fall back to the
      // aggregator giftPromotion, so a failed call never shows a stale gift list.
      return {
        ...state,
        isSlaFetching: false,
        slaFetchFailed: true,
        slaDetails: {
          ...state.slaDetails,
          [pinCode]: {
            ...(state.slaDetails[pinCode] || {}),
            [skuId]: { options: [], isFetching: false, error: true, hasMoreSellers: false }
          }
        }
      };
    }
    case EDDAction.FETCH.SELLER_LIST_SLA.REQUEST: {
      const { pinCode, skuId } = action;
      return {
        ...state,
        sellerListSla: {
          ...state.sellerListSla,
          [pinCode]: {
            ...((state.sellerListSla || {})[pinCode] || {}),
            [skuId]: {
              ...(((state.sellerListSla || {})[pinCode] || {})[skuId] || {}),
              isFetching: true,
              error: false
            }
          }
        }
      };
    }

    case EDDAction.FETCH.SELLER_LIST_SLA.SUCCESS: {
      const { pinCode, skuId, requestedSkuId, options = [] } = action;
      const settledEntry = { options, isFetching: false, error: false };
      return {
        ...state,
        sellerListSla: {
          ...state.sellerListSla,
          [pinCode]: {
            ...((state.sellerListSla || {})[pinCode] || {}),
            // Same key-settling as the v2 slice: the response can come back
            // under a code other than the one requested.
            ...(requestedSkuId && requestedSkuId !== skuId
              ? { [requestedSkuId]: settledEntry }
              : {}),
            [skuId]: settledEntry
          }
        }
      };
    }

    case EDDAction.FETCH.SELLER_LIST_SLA.FAILURE: {
      const { pinCode, skuId } = action;
      return {
        ...state,
        sellerListSla: {
          ...state.sellerListSla,
          [pinCode]: {
            ...((state.sellerListSla || {})[pinCode] || {}),
            [skuId]: { options: [], isFetching: false, error: true }
          }
        }
      };
    }

    case EDDAction.FETCH.DELIVERY_DETAILS.SUCCESS: {
      const { pinCode, productDetails, servicability, productCode } = action;
      let productDeliveryDetails = productDetails.reduce((acc, product)=> {
        if(product.deliveryMethod === "STANDARD") {
          return {
            ...acc,
            ...product
          }
        } else if(product.deliveryMethod === "PRIORITY") {
          return {
            ...acc,
            priorityDelivery: product
          }
        }
        else if(product.deliveryMethod === "RUSH " || product.deliveryMethod === "RUSH") {
          return {
            ...acc,
            rushDelivery: product
          }
        }
      }, {});

      if (servicability) {
        const successStatus = {
          ...state,
          isEDDFetching: false,
          activePincode: pinCode,
          isCheckDeliveryStatusCalled: true,
          eddDetails: {
            ...state.eddDetails,
            [action.pinCode]: {
              ...(state.eddDetails[pinCode] || {}),
              [productDetails[0].productCode]: productDeliveryDetails
            }
          }
        };
        return successStatus;
      }

      const errorStaus = {
        ...state,
        isEDDFetching: false,
        activePincode: pinCode,
        eddDetails: {
          ...state.eddDetails,
          [pinCode]: {
            ...(state.eddDetails[pinCode] || {}),
            [productDetails[0].productCode]: {
              ...productDetails[0]
            }
          }
        }
      };
      return errorStaus;
    }

    case EDDAction.FETCH.PRODUCT_SERVICEABILITY_DETAILS.REQUEST: {
      return {
        ...state,
        isServiceabilityFetching: true,
       };
    }

    case EDDAction.FETCH.PRODUCT_SERVICEABILITY_DETAILS.SUCCESS: {
      const {
        payload: { cod = {}, returnWindow = {}, showEmi = false }
      } = action;
      const isCodEmpty = _isEmpty(cod)
      const isRexEmpty = _isEmpty(returnWindow)
      let successStatus = {};
       if (isCodEmpty && isRexEmpty) {
        console.log("cod and rex empty")
        successStatus = {
          ...state,
          isServiceabilityFetching: false,
          isCheckServiceabilityCalled: false,
          isCheckServiceabilityCalledForRex: false,
          errorInServiceabilityFetching: false,
          serviceabilityDetails: {
            ...state.serviceabilityDetails,
            cod,
            returnWindow,
            showEmi
          }
        };
      }
      else if (isCodEmpty) {
        console.log("cod empty")
        successStatus = {
          ...state,
          isServiceabilityFetching: false,
          isCheckServiceabilityCalled: false,
          isCheckServiceabilityCalledForRex: true,
          errorInServiceabilityFetching: false,
          serviceabilityDetails: {
            ...state.serviceabilityDetails,
            cod,
            returnWindow,
            showEmi
          }
        };
      } else if(isRexEmpty){
        console.log("rex empty")

        successStatus = {
          ...state,
          isServiceabilityFetching: false,
          isCheckServiceabilityCalled: true,
          isCheckServiceabilityCalledForRex: false,
          errorInServiceabilityFetching: false,
          serviceabilityDetails: {
            ...state.serviceabilityDetails,
            cod,
            returnWindow,
            showEmi
          }
        };
      } else {
        console.log("None empty")
        successStatus = {
          ...state,
          isServiceabilityFetching: false,
          isCheckServiceabilityCalled: true,
          isCheckServiceabilityCalledForRex: true,
          errorInServiceabilityFetching: false,
          serviceabilityDetails:{
            ...state.serviceabilityDetails,
            cod,
            returnWindow,
            showEmi
          }
        };
      }
      return successStatus;
    }
    case EDDAction.FETCH.PRODUCT_SERVICEABILITY_DETAILS.FAILURE: {
      return {
        ...state,
        isServiceabilityFetching: false,
        errorInServiceabilityFetching: true
      };
    }
    case EDDAction.FETCH.CART_ITEMS_DELIVERY_DETAILS.REQUEST: {
      return { ...state, isCartItemsEDDFetching: true };
    }
    case EDDAction.FETCH.CART_ITEMS_DELIVERY_DETAILS.FAILURE: {
      return { ...state, isCartItemsEDDFetching: false };
    }
    case 'CHECK_SERVICEABILITY_API_FAILED': {
      return { ...state, isCheckServiceabilityAPIFailed: true };
    }
    case 'CHECK_SERVICEABILITY_API_SUCCESS': {
      return { ...state, isCheckServiceabilityAPIFailed: false };
    }
    case EDDAction.FETCH.CART_ITEMS_DELIVERY_DETAILS.SUCCESS: {
      const { productDetails, pincode, servicability, codEligible } = action;

      const nextState = {
        ...state,
        servicability,
        codEligible,
        activePincode: pincode,
        isCartItemsEDDFetching: false,
        eddDetails: {
          ...state.eddDetails,
          [pincode]: {
            // ...state.eddDetails[pinCode] || {},
            ...[...productDetails].reduce((productMap, itemEDDDetails) => {
              return {
                ...productMap,
                [itemEDDDetails.productCode]: itemEDDDetails
              };
            }, {})
          }
        }
      };
      return nextState;
    }
    case EDDAction.MOVE.ALL_NON_DELIVERABLE_ITEMS_TO_CLOSET.REQUEST: {
      return { ...state, movingAllNonDeliverableItemsToCloset: true };
    }
    case EDDAction.MOVE.ALL_NON_DELIVERABLE_ITEMS_TO_CLOSET.FAILURE:
    case EDDAction.MOVE.ALL_NON_DELIVERABLE_ITEMS_TO_CLOSET.SUCCESS: {
      return { ...state, movingAllNonDeliverableItemsToCloset: false };
    }

    case EDDAction.REMOVE.NON_VELIVERABLE_ITEM: {
      const { activePincode, productCode: code } = action;
      return {
        ...state,
        eddDetails: {
          ...state.eddDetails,
          [activePincode]: Object.entries(state.eddDetails[activePincode]).reduce(
            (newOptions, [productCode, productDetails]) => {
              if (productCode.toString() !== code.toString()) {
                return {
                  ...newOptions,
                  [productCode]: productDetails
                };
              }
              return newOptions;
            },
            {}
          )
        }
      };
    }
    default:
      return state;
  }
}
export default edd;
